import React, { useEffect, useState } from "react";
import { ScreenInfoApiResponse } from "../../../../PublicScreens.types";
import ReactSelect from "../../../../../ControlHub/components/ReactSelect";
import FormInput from "../../../FormInput";
import HourlyPricesModal from "../HourlyPricesModal";
import FormField from "../../../FormField";

interface ScreenDetailsProps {
  screenInfo: ScreenInfoApiResponse;
  screenMode: "view" | "edit";
  setScreenMode: (mode: "view" | "edit") => void;
  setScreenInfo: React.Dispatch<
    React.SetStateAction<ScreenInfoApiResponse | null>
  >;
  onSave: () => Promise<void>;
}

const ScreenDetails: React.FC<ScreenDetailsProps> = ({
  screenInfo,
  screenMode,
  setScreenMode,
  setScreenInfo,
  onSave,
}) => {
  const [showHourlyPrices, setShowHourlyPrices] = useState(false);

  const TYPE = {
    TV: "Screen",
    CHANNEL: "Channel",
  };

  const resolutionOptions = [
    { label: "720 PX", value: "P720" },
    { label: "1080 PX", value: "P1080" },
    { label: "2K", value: "K2" },
    { label: "4K", value: "K4" },
  ];

  const orientationOptions = [
    { label: "Landscape", value: "LANDSCAPE" },
    { label: "Portrait", value: "PORTRAIT" },
  ];

  const SCREEN_CATEGORY = {
    PREMIUM: "Premium",
    BASIC: "Basic",
  };

  const selectedResolution =
    resolutionOptions.find((opt) => opt.value === screenInfo.resolution) ||
    null;

  const DAY_MAP: Record<string, string> = {
    "1": "Mon",
    "2": "Tue",
    "3": "Wed",
    "4": "Thu",
    "5": "Fri",
    "6": "Sat",
    "7": "Sun",
  };

  const HOURS = Array.from({ length: 14 }, (_, i) => i + 8); // 8AM → 10PM

  const DUMMY_HOURLY_PRICES = {
    "1": { "10": 1.0, "11": 1.0, "12": 1.2 },
    "3": { "14": 1.5, "15": 1.5 },
    "6": { "10": 2.0, "11": 2.0 },
  };

  const hourlyPricesData = screenInfo.hourlyPrices ?? DUMMY_HOURLY_PRICES;

  const typeOptions = [
    { label: "Screen", value: "TV" },
    { label: "Channel", value: "CHANNEL" },
  ];
  const selectedType =
    typeOptions.find((opt) => opt.value === screenInfo.type) || null;

  const SCREEN_SIZE_OPTIONS = [
    { label: "24", value: "I24" },
    { label: "32", value: "I32" },
    { label: "40", value: "I40" },
    { label: "43", value: "I43" },
    { label: "50", value: "I50" },
    { label: "55", value: "I55" },
    { label: "65", value: "I65" },
    { label: "75", value: "I75" },
    { label: "85", value: "I85" },
  ];

  const selectedSize =
    SCREEN_SIZE_OPTIONS.find((opt) => opt.value === screenInfo.size) || null;

  const SCREEN_STATUS_OPTIONS = [
    { label: "Active", value: "ACTIVE" },
    { label: "Inactive", value: "INACTIVE" },
  ];
  const selectedStatus =
    SCREEN_STATUS_OPTIONS.find((opt) => opt.value === screenInfo.status) ||
    null;

  const SCREEN_CATEGORY_OPTIONS = [
    { label: "Basic", value: "BASIC" },
    { label: "Premium", value: "PREMIUM" },
  ];
  const selectedScreenCategory =
    SCREEN_CATEGORY_OPTIONS.find(
      (opt) => opt.value === screenInfo.screenCategory,
    ) || null;

  return (
    <div className="rounded-xl border bg-white p-4">
      <div className="flex justify-between mb-3 items-center">
        <h3 className="text-sm font-semibold text-gray-500">
          Screen Information
        </h3>

        <div className="flex gap-2">
          <button
            onClick={() => setScreenMode("view")}
            className={`px-3 py-2 text-xs rounded-md border ${
              screenMode === "view"
                ? "bg-black text-white"
                : "bg-white text-gray-600"
            }`}
          >
            View
          </button>
          <button
            onClick={() => setScreenMode("edit")}
            className={`px-3 py-2 text-xs rounded-md border ${
              screenMode === "edit"
                ? "bg-black text-white"
                : "bg-white text-gray-600"
            }`}
          >
            Edit
          </button>
          {screenMode === "edit" && (
            <button
            onClick={onSave}
              className={`px-3 py-2 text-xs rounded-md border bg-green-500 text-white transition-all duration-300`}
            >
              Save
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label="Screen Name">
          <FormInput
            value={screenInfo.name}
            readOnly={screenMode === "view"}
            onChange={(val) => setScreenInfo({ ...screenInfo, name: val })}
          />
        </FormField>

        {/* <FormInput label="Screen ID" value={screenId!} readOnly /> */}

        <FormField label="User ID">
          <FormInput value={screenInfo.userId} readOnly />
        </FormField>

        <FormField label="Org ID">
          <FormInput value={screenInfo.orgId} readOnly />
        </FormField>
        <FormField label="Store ID">
          <FormInput value={screenInfo.storeId} readOnly />
        </FormField>

        <FormField label="Type">
          <ReactSelect
            options={typeOptions}
            value={selectedType}
            onChange={(option) => {
              if (!option) return;
              setScreenInfo({
                ...screenInfo,
                type: option.value as ScreenInfoApiResponse["type"],
              });
            }}
            isDisabled={screenMode === "view"}
          />
        </FormField>

        <FormField label="Size">
          <ReactSelect
            options={SCREEN_SIZE_OPTIONS}
            value={selectedSize}
            onChange={(option) => {
              if (!option) return;
              setScreenInfo({
                ...screenInfo,
                size: option.value as ScreenInfoApiResponse["size"],
              });
            }}
            isDisabled={screenMode === "view"}
          />
        </FormField>

        <FormField label="Resolution">
          <ReactSelect
            options={resolutionOptions}
            value={selectedResolution}
            onChange={(option) => {
              if (!option) return;
              setScreenInfo({
                ...screenInfo,
                resolution: option.value as ScreenInfoApiResponse["resolution"],
              });
            }}
            isDisabled={screenMode === "view"}
          />
        </FormField>

        <FormField label="Orientation">
          <ReactSelect
            options={orientationOptions}
            value={
              orientationOptions.find(
                (o) => o.value === screenInfo.orientation,
              ) || null
            }
            onChange={(option) => {
              if (!option) return;
              setScreenInfo({
                ...screenInfo,
                orientation:
                  option.value as ScreenInfoApiResponse["orientation"],
              });
            }}
            isDisabled={screenMode === "view"}
          />
        </FormField>

        <FormField label="Screen Category">
          <ReactSelect
            options={SCREEN_CATEGORY_OPTIONS}
            value={selectedScreenCategory}
            onChange={(option) => {
              if (!option) return;
              setScreenInfo({
                ...screenInfo,
                screenCategory:
                  option.value as ScreenInfoApiResponse["screenCategory"],
              });
            }}
            isDisabled={screenMode === "view"}
          />
        </FormField>

        <FormField label="Merchant Ads Quota">
          <FormInput
            value={screenInfo.merchantAdsQuota}
            readOnly={screenMode === "view"}
            onChange={(val) =>
              setScreenInfo({
                ...screenInfo,
                merchantAdsQuota: Number(val),
              })
            }
          />
        </FormField>

        <FormField label="Visibility">
          <FormInput
            value={screenInfo.isPublic ? "Public" : "Private"}
            readOnly
          />
        </FormField>

        <FormField label="Status">
          <ReactSelect
            options={SCREEN_STATUS_OPTIONS}
            value={selectedStatus}
            onChange={(option) => {
              if (!option) return;
              setScreenInfo({
                ...screenInfo,
                status: option.value as ScreenInfoApiResponse["status"],
              });
            }}
            isDisabled={screenMode === "view"}
          />
        </FormField>

        {/* Hourly Prices */}
        {screenInfo?.hourlyPrices && (
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => setShowHourlyPrices(true)}
              className="px-4 py-2 text-xs rounded-md border bg-gray-100 hover:bg-gray-200"
            >
              View Hourly Prices
            </button>
          </div>
        )}
      </div>
      {
        <HourlyPricesModal
          open={showHourlyPrices}
          onClose={() => setShowHourlyPrices(false)}
          hourlyPrices={hourlyPricesData}
          dayMap={DAY_MAP}
          hours={HOURS}
        />
      }
    </div>
  );
};

export default ScreenDetails;
